import type { ReactElement } from "react";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, ChevronLeft, ChevronRight, List, Heart, AlertCircle, CheckCircle } from "lucide-react";
import PageLayout from "../components/PageLayout";

/**
 * Content policy section
 */
interface ContentSection {
  id: string;
  title: string;
  shortTitle: string;
  content: string;
  type?: "positive" | "negative" | "neutral";
}

const LAST_UPDATED = "January 31, 2026";

const SECTIONS: ContentSection[] = [
  {
    id: "introduction",
    title: "1. Our Commitment to Safe Storytelling",
    shortTitle: "Introduction",
    type: "positive",
    content: `At Mytho Labs, we believe in the magic of storytelling that inspires, educates, and delights children and families. Our Tellor platform is designed to be a safe, creative space where imagination can flourish.

This Content Policy outlines the standards for content created on our platform. These guidelines help ensure that all stories generated through our Service remain appropriate, safe, and enriching for children of all ages.

By using our Service, you agree to follow these content guidelines. Content that violates this policy may be removed, and repeated violations may result in account restrictions.`,
  },
  {
    id: "encouraged-content",
    title: "2. Content We Encourage ✨",
    shortTitle: "Encouraged",
    type: "positive",
    content: `We love stories that:

Educational and Inspiring Themes:
• Friendship, kindness, and empathy
• Problem-solving and creative thinking
• Learning about the world (animals, nature, science)
• Cultural diversity and inclusion
• Overcoming challenges through perseverance
• Family bonds and relationships

Positive Character Traits:
• Brave heroes who help others
• Characters who learn from mistakes
• Stories about teamwork and cooperation
• Adventures that teach valuable lessons
• Characters who celebrate differences

Age-Appropriate Adventures:
• Magical journeys to fantastical places
• Friendly animals and creatures
• Space exploration and discovery
• Gentle mysteries and puzzles
• Seasonal celebrations and traditions
• Stories that celebrate imagination

Inclusive Storytelling:
• Characters of all backgrounds and abilities
• Stories that represent diverse families
• Adventures that everyone can enjoy
• Content that makes all children feel welcome`,
  },
  {
    id: "prohibited-content",
    title: "3. Content We Don't Allow ❌",
    shortTitle: "Prohibited",
    type: "negative",
    content: `To maintain a safe environment for children, we prohibit content containing:

Violence and Harm:
• Graphic violence or detailed descriptions of injury
• Content that promotes self-harm or dangerous behaviors
• Bullying, harassment, or meanness between characters
• Weapons used to hurt others (beyond fantasy swords/wands)
• Scary or disturbing imagery that could frighten young children

Inappropriate Adult Content:
• Sexual content or romantic relationships beyond innocent crushes
• Adult themes not suitable for children
• Drug or alcohol use (except adults drinking coffee/tea in background)
• Gambling or inappropriate financial activities

Harmful Stereotypes:
• Content that promotes discrimination or prejudice
• Negative stereotypes about any group of people
• Content that mocks disabilities or differences
• Stories that promote exclusion or intolerance

Dangerous Activities:
• Instructions for dangerous stunts or experiments
• Content encouraging risky behavior
• Detailed descriptions of how to make dangerous items
• Activities that could lead to real-world harm

Inappropriate Language:
• Profanity or crude language
• Hate speech or slurs
• Language intended to frighten or upset children
• Adult humor inappropriate for young audiences

Copyright Infringement:
• Unauthorized use of copyrighted characters
• Direct copying of existing stories or books
• Using trademarked names without permission
• Reproducing protected content without rights`,
  },
  {
    id: "character-guidelines",
    title: "4. Character Creation Guidelines",
    shortTitle: "Characters",
    type: "neutral",
    content: `When creating characters for your stories, please follow these guidelines:

Appropriate Character Design:
• Characters should be family-friendly in appearance
• Avoid overly scary or frightening character designs
• Ensure characters model positive behaviors
• Create diverse and inclusive character representations

Original Characters:
• Create your own unique characters rather than copying existing ones
• You may draw inspiration from archetypes (brave knight, wise owl) without copying specific copyrighted characters
• Give your characters original names and personalities
• Develop character traits that align with positive values

Character Behavior:
• Characters should demonstrate kindness and respect
• Show characters working together to solve problems
• Avoid characters who are consistently mean or harmful
• Include characters who learn and grow throughout the story

Character Relationships:
• Focus on friendships, family bonds, and mentorships
• Keep romantic content limited to innocent crushes suitable for children
• Show healthy conflict resolution between characters
• Emphasize cooperation over competition`,
  },
  {
    id: "series-guidelines",
    title: "5. Series and Continuing Stories",
    shortTitle: "Series",
    type: "neutral",
    content: `Our platform's unique series feature allows you to create ongoing adventures with consistent characters:

Building Your Series:
• Develop character growth and development over time
• Create interconnected stories that build on previous adventures
• Maintain consistency in character personalities and world-building
• Plan story arcs that teach different lessons

Series Content Standards:
• All individual stories must comply with our content guidelines
• Ensure that series progression remains age-appropriate
• Avoid storylines that become progressively darker or inappropriate
• Maintain the educational or entertainment value throughout the series

Character Continuity:
• Keep character designs and personalities consistent across stories
• Show positive character development and learning
• Maintain the child-friendly nature of all characters throughout the series
• Ensure that returning characters continue to model good behavior`,
  },
  {
    id: "ai-safety",
    title: "6. AI Safety and Content Filtering",
    shortTitle: "AI Safety",
    type: "neutral",
    content: `Our AI systems are designed with safety as a top priority:

Automated Safety Measures:
• Content filters automatically screen all story prompts
• AI models are trained to avoid generating inappropriate content
• Visual generation includes safety checks for child-appropriate imagery
• Multiple layers of safety systems work together to protect users

Human Review Process:
• Flagged content may be reviewed by human moderators
• Our team regularly audits AI outputs for safety and appropriateness
• Community reports are promptly investigated
• Content that violates policies is removed quickly

Continuous Improvement:
• We regularly update our safety systems based on new challenges
• User feedback helps us improve our content filtering
• We work with child safety experts to enhance our protections
• Our AI models are continuously refined to maintain safety standards

What to Do If You See Concerning Content:
• Report inappropriate content immediately using our reporting tools
• Do not share concerning content with others
• Contact our support team for serious safety concerns
• Help us maintain a safe community by being vigilant`,
  },
  {
    id: "user-responsibilities",
    title: "7. User Responsibilities",
    shortTitle: "Responsibilities",
    type: "neutral",
    content: `All users of the Tellor platform have important responsibilities:

For Adult Users:
• Supervise children's use of the platform
• Review stories created by or shared with children
• Report inappropriate content immediately
• Ensure that prompts entered are appropriate for children
• Model good digital citizenship

For Young Users:
• Only create stories that would make you and your family proud
• Treat other users with kindness and respect
• Ask for help if you see something that doesn't feel right
• Follow the same rules of kindness online that you follow in real life
• Remember that your stories might be seen by other children

For Educators and Parents:
• Use the platform to supplement learning and creativity
• Guide children in creating educational and inspiring stories
• Discuss digital citizenship and appropriate online behavior
• Help children understand the importance of inclusive storytelling
• Report any concerns to platform moderators promptly`,
  },
  {
    id: "reporting",
    title: "8. Reporting and Enforcement",
    shortTitle: "Reporting",
    type: "neutral",
    content: `We take content policy violations seriously and encourage reporting:

How to Report Content:
• Use the "Report" button on any story or content
• Email support@mytho-labs.com with "Content Report" in the subject line
• Provide specific details about the concerning content
• Include the story ID or user information if available

What Happens After a Report:
• All reports are reviewed promptly by our moderation team
• Content that violates policies is removed immediately
• Users who violate policies may receive warnings or account restrictions
• Serious violations may result in permanent account suspension

Response Timeline:
• Urgent safety concerns: Within 1 hour during business hours
• General policy violations: Within 24 hours
• Complex cases requiring investigation: Within 72 hours
• Appeals and clarifications: Within 5 business days

Appealing Moderation Decisions:
• Contact support@mytho-labs.com with "Appeal" in the subject line
• Provide your reasoning for why the content should be reinstated
• Include any additional context that might be relevant
• Appeals are reviewed by senior moderation staff`,
  },
  {
    id: "consequences",
    title: "9. Policy Violations and Consequences",
    shortTitle: "Consequences",
    type: "negative",
    content: `We enforce these guidelines to maintain a safe community for all users:

Progressive Enforcement:
• First violation: Warning and educational resources
• Repeated violations: Temporary account restrictions
• Serious violations: Permanent account suspension
• Illegal content: Immediate suspension and law enforcement notification

Types of Restrictions:
• Content creation limits
• Temporary suspension of story generation
• Removal of sharing privileges
• Restriction of community features
• Complete account suspension for severe violations

Factors in Enforcement Decisions:
• Severity of the policy violation
• Intent behind the violation (accidental vs. deliberate)
• User's history of violations
• Impact on other users, especially children
• Potential harm to the community

Educational Approach:
• We prefer education over punishment when possible
• Users receive information about why content was problematic
• Resources are provided to help users understand appropriate content
• Second chances are given for users who demonstrate understanding`,
  },
  {
    id: "updates",
    title: "10. Policy Updates and Community Input",
    shortTitle: "Updates",
    type: "neutral",
    content: `This Content Policy evolves to meet the needs of our community:

Regular Reviews:
• We review and update this policy quarterly
• Changes are based on community feedback and safety research
• New challenges in AI content generation are addressed promptly
• Child safety experts provide ongoing guidance

Community Involvement:
• User feedback helps shape policy updates
• Educators and parents provide valuable input on age-appropriateness
• Regular surveys help us understand community needs
• Transparency reports share information about policy enforcement

Notification of Changes:
• Significant policy changes are announced via email
• Updates are highlighted on the platform
• Users are given time to review and understand new guidelines
• Support is available to answer questions about policy changes`,
  },
  {
    id: "contact",
    title: "11. Questions and Support",
    shortTitle: "Contact",
    type: "positive",
    content: `We're here to help you create amazing, appropriate stories:

Getting Help:
• Email: support@mytho-labs.com
• Subject line tags: "Content Question," "Policy Clarification," "Safety Concern"
• Response time: Within 24 hours for general inquiries, faster for safety issues

Resources Available:
• Story prompt suggestions for age-appropriate content
• Guidelines for creating inclusive characters
• Tips for building educational series
• Examples of positive story themes

Building a Better Community:
• Share feedback on how we can improve our content policies
• Suggest new features that promote positive storytelling
• Report issues that help us enhance safety measures
• Participate in community discussions about creative storytelling

Remember: Our goal is to empower creativity while keeping everyone safe. When in doubt, ask yourself: "Would I be comfortable sharing this story with children and families?" If the answer is yes, you're on the right track! ✨`,
  },
];

/**
 * ContentPolicyPage Component
 *
 * Content policy and community guidelines page
 */
const ContentPolicyPage = (): ReactElement => {
  const navigate = useNavigate();
  const [showToc, setShowToc] = useState<boolean>(false);

  const scrollToSection = (id: string): void => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setShowToc(false);
  };

  const getSectionIcon = (type?: string) => {
    switch (type) {
      case "positive":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "negative":
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Heart className="w-4 h-4 text-orange-600" />;
    }
  };

  return (
    <PageLayout>
      <div className="min-h-screen pb-16" style={{ background: "var(--st-bg-page)" }}>
        {/* Back Button */}
        <div className="max-w-3xl mx-auto px-4 pt-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm mb-4 transition-all hover:opacity-70"
            style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        {/* Page Header Card */}
        <div className="max-w-3xl mx-auto px-4">
          <div
            className="rounded-2xl p-6 mb-6"
            style={{
              background: "var(--st-bg-surface)",
              border: "1px solid var(--st-border-light)",
              boxShadow: "0 4px 12px rgba(92, 64, 51, 0.06)",
            }}
          >
            <div className="flex flex-col items-center text-center">
              <div
                className="w-16 h-16 mb-4 rounded-2xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                }}
              >
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h1
                className="text-2xl md:text-3xl font-bold mb-2"
                style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
              >
                Content Policy & Community Guidelines
              </h1>
              <p
                className="text-sm mb-2"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                Creating safe, inspiring stories for everyone
              </p>
              <p
                className="text-xs"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                Last updated: {LAST_UPDATED}
              </p>

              {/* Table of Contents Toggle - Mobile */}
              <button
                onClick={() => setShowToc(!showToc)}
                className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02] md:hidden"
                style={{
                  background: "var(--st-bg-page)",
                  border: "1px solid var(--st-border-light)",
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                <List className="w-4 h-4" />
                {showToc ? "Hide" : "Show"} Contents
              </button>
            </div>
          </div>
        </div>

        {/* Mobile TOC Dropdown */}
        {showToc && (
          <div className="max-w-3xl mx-auto px-4 mb-6 md:hidden">
            <div
              className="rounded-xl p-4"
              style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
            >
              <div className="space-y-1">
                {SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-all hover:bg-orange-50 dark:hover:bg-orange-950/30"
                    style={{
                      fontFamily: "Quicksand, sans-serif",
                      color: "var(--st-text-heading)",
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {getSectionIcon(section.type)}
                      {section.shortTitle}
                    </div>
                    <ChevronRight className="w-4 h-4" style={{ color: "var(--st-text-muted)" }} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex gap-6">
            {/* Desktop Table of Contents - Sticky Sidebar */}
            <div className="hidden md:block w-56 flex-shrink-0">
              <div
                className="sticky top-24 rounded-xl p-4"
                style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
              >
                <p
                  className="text-xs font-semibold mb-3 uppercase tracking-wide"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-muted)",
                  }}
                >
                  Guidelines
                </p>
                <div className="space-y-1">
                  {SECTIONS.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className="w-full text-left px-2 py-1.5 rounded-lg text-xs transition-all hover:bg-orange-50 dark:hover:bg-orange-950/30"
                      style={{
                        fontFamily: "Quicksand, sans-serif",
                        color: "var(--st-text-heading)",
                      }}
                    >
                      <div className="flex items-center gap-2">
                        {getSectionIcon(section.type)}
                        <span className="truncate">{section.shortTitle}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <div
                className="rounded-xl p-6 md:p-8"
                style={{
                  background: "var(--st-bg-surface)",
                  border: "1px solid var(--st-border-light)",
                  boxShadow: "0 4px 12px rgba(92, 64, 51, 0.06)",
                }}
              >
                {/* Sections */}
                <div className="space-y-8">
                  {SECTIONS.map((section) => (
                    <div key={section.id} id={section.id} className="scroll-mt-24">
                      <div className="flex items-center gap-3 mb-3">
                        {getSectionIcon(section.type)}
                        <h2
                          className="text-lg font-bold"
                          style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
                        >
                          {section.title}
                        </h2>
                      </div>
                      <div
                        className="text-sm leading-relaxed whitespace-pre-wrap"
                        style={{
                          fontFamily: "Quicksand, sans-serif",
                          color: "var(--st-text-heading)",
                        }}
                      >
                        {section.content}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="max-w-3xl mx-auto px-4 mt-8">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => {
                navigate("/terms");
                window.scrollTo(0, 0);
              }}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-[var(--st-bg-surface)]"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              Terms of Service
            </button>
            <span style={{ color: "var(--st-border-light)" }}>|</span>
            <button
              onClick={() => {
                navigate("/privacy");
                window.scrollTo(0, 0);
              }}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-[var(--st-bg-surface)]"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              Privacy Policy
            </button>
            <span style={{ color: "var(--st-border-light)" }}>|</span>
            <button
              onClick={() => {
                navigate("/dmca");
                window.scrollTo(0, 0);
              }}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-[var(--st-bg-surface)]"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              DMCA Policy
            </button>
            <span style={{ color: "var(--st-border-light)" }}>|</span>
            <button
              onClick={() => {
                navigate("/contact");
                window.scrollTo(0, 0);
              }}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-[var(--st-bg-surface)]"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default ContentPolicyPage;